import { Component, inject } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import {
  AccessPermissionService,
  mainModuleName,
  ProductReportFormatEnum,
  ReportFormLabels,
  ReportFormPlaceholders,
  rolesModuleName,
} from '@piqet-angular/shared';
import * as saveAs from 'file-saver';
import { ReportsService } from 'libs/data/src/lib/services/report/reports.service';
import { SharedService } from 'libs/data/src/lib/services/shared/shared.service';
import { ToastrService } from 'ngx-toastr';
import { ViewportScroller } from '@angular/common';
import { customDecode, getRolesAndPermission } from '@piqet-angular/data';
import { ActivatedRoute } from '@angular/router';

// Add interface for BE payload
interface GetProductExcelReportViewModel {
  productConfigId: number;
  reportName: string;
  reportFormatId: number | null;
  reportTypeId: number | null;
  productIds: string;
  funtionalUnitId: number;
  baseProductId: number;
  indicatorCategoryId: number;
  indicatorIds: string;
}

// Add interface for API response items
interface SelectableItem {
  id: number;
  idStr?: string;
  name: string;
  description?: string;
  selected?: boolean;
  [key: string]: any;
}

@Component({
  selector: 'piqet-angular-product-report-generation',
  templateUrl: './product-report-generation.component.html',
  styleUrls: ['./product-report-generation.component.scss'],
})
export class ProductReportGenerationComponent {
  readonly REPORT_FORM_PLACEHOLDER = ReportFormPlaceholders;
  readonly REPORT_FORM_LABEL = ReportFormLabels;

  // properties
  private readonly CUSTOM_CATEGORIES = 'Customize categories';
  readonly EXPORT_RAW_DATA = 3; //Export raw data
  readonly EXPORT_RAW_DATA_TYPE_4 = 4; //Export raw data (same behaviour as type 3)
  readonly EXPORT_RAW_DATA_NEW = 6; //Export raw data (new endpoint)

  reportForm!: FormGroup;
  reportFormateList: any;
  reportTypeList = ProductReportFormatEnum;
  reportScenarioList: any;
  maxAllowedSelectedItems: number = 6;
  chooseUpToScenarioMessage: string = 'Select up to 6 scenarios';
  functionalUnitList: any;
  baseScenarioList: any;
  impactCategoriesList: any;
  buttonName: string = 'Generate';
  finalSubmit: boolean = false;
  productConfig: any;
  initialReportFormData: any;
  customCategoriesList: any;
  isShowCustomCategories: boolean = false;
  // injects
  fb = inject(FormBuilder);
  isSelectedReportExport: boolean = false;
  showCustomProjectSetting = false;
  activatedRoute: any;
  showEdit = false;

  constructor(
    private readonly sharedService: SharedService,
    private readonly _reportService: ReportsService,
    private readonly toast: ToastrService,
    private readonly viewportScroller: ViewportScroller,
    private readonly accessService: AccessPermissionService,
    private readonly route: ActivatedRoute
  ) {
    this.sharedService.scenarioNameFunc('Report generation');
    this.activatedRoute = this.route.snapshot.routeConfig?.path;
    const url = location.href.split('/');
    this.productConfig = Number(atob(decodeURIComponent(customDecode(url[6]))));
    this.sharedService.accessProductSideBarData(() => {});
    this.initializeForm();
  }

  private initializeForm() {
    this.reportForm = this.fb.group({
      reportTitle: ['', [Validators.required]],
      reportFormate: [null, [Validators.required]],
      reportType: [{ value: null, disabled: false }, [Validators.required]],
      reportScenarios: [null, [Validators.required]],
      functionalUnit: [null, [Validators.required]],
      baseScenario: [null, [Validators.required]],
      impactCategories: [null, [Validators.required]],
      categoriesToInclude: [null],
    });
  }

  async ngOnInit() {
    this.showEdit = this.accessService.hasPermission('edit', this.activatedRoute);
    this.showCustomProjectSetting = getRolesAndPermission(
      rolesModuleName.myOrganizationProjectReportSetting,
      mainModuleName.advancedModule
    )?.isOverview;
    this.viewportScroller.scrollToPosition([0, 0]);
    await this.getReportFormData();
    this.setupFormSubscriptions();
  }

  private setupFormSubscriptions() {
    // Handle report format changes
    this.reportForm.get('reportFormate')?.valueChanges.subscribe(format => {
      const reportTypeControl = this.reportForm.get('reportType');
      const formatId = Number(format);
      if (
        formatId === this.EXPORT_RAW_DATA ||
        formatId === this.EXPORT_RAW_DATA_TYPE_4 ||
        formatId === this.EXPORT_RAW_DATA_NEW
      ) {
        this.isSelectedReportExport = true;
        reportTypeControl?.disable();
        this.reportForm.get('reportType')?.patchValue(null);
        reportTypeControl?.clearValidators();
      } else {
        this.isSelectedReportExport = false;
        reportTypeControl?.enable();
        reportTypeControl?.setValidators([Validators.required]);
      }
      reportTypeControl?.updateValueAndValidity();
    });

    // Handle impact categories changes
    this.reportForm.get('impactCategories')?.valueChanges.subscribe(value => {
      if (value) {
        // Use setTimeout to avoid call stack issues
        setTimeout(() => {
          this.isShowCustomCategories = value.name === this.CUSTOM_CATEGORIES;
          this.validateCustomCategoriesControl();
        }, 0);
      }
    });
  }

  onCustomCategoriesChange(event: any) {
    if (event?.id) {
      this.reportForm.get('categoriesToInclude')?.setValue(null);
    }
  }

  validateCustomCategoriesControl() {
    const categoriesToIncludeControl = this.reportForm.get('categoriesToInclude');
    if (this.isShowCustomCategories) {
      categoriesToIncludeControl?.setValidators([Validators.required]);
    } else {
      categoriesToIncludeControl?.clearValidators();
    }
    categoriesToIncludeControl?.updateValueAndValidity();
  }

  private findSelectedItem(items: SelectableItem[]): SelectableItem | null {
    return items?.find(item => item.selected === true) || null;
  }

  private findSelectedItems(items: SelectableItem[]): any[] {
    return items?.filter(item => item.selected === true) || [];
  }

  private extractSelectedValues(items: SelectableItem[], valueProperty: string): any[] {
    const selectedItems = this.findSelectedItems(items);
    return selectedItems.map(item => item[valueProperty]).filter(val => val !== undefined);
  }

  async getReportFormData() {
    try {
      const res = await this._reportService.getProductReportFormData(this.productConfig);
      this.initialReportFormData = res.data;

      // Map the API response fields to component properties
      // Handle new field names from the response
      this.reportFormateList =
        this.initialReportFormData.reportFormatItems ||
        this.initialReportFormData.newReporFormatList;

      this.functionalUnitList = this.initialReportFormData.functionalUnitItems;
      this.impactCategoriesList = this.initialReportFormData.impactCategoryItems;
      this.customCategoriesList = this.initialReportFormData.indicatorItems;

      // Handle both old and new response structures for products
      this.reportScenarioList =
        this.initialReportFormData.productItems || this.initialReportFormData.selectedItemIds;

      // Set base scenario list (all products can be base scenarios)
      this.baseScenarioList = [...this.reportScenarioList];

      this.setFormData();
    } catch (error) {
      console.error('Error fetching report form data:', error);
    }
  }

  private setFormData(): void {
    // Extract report title
    const reportTitle =
      this.initialReportFormData.reportTitle ||
      this.initialReportFormData.productConfigurationName ||
      '';

    // Find selected report format
    const selectedReportFormat = this.findSelectedItem(this.reportFormateList);

    // Find selected functional unit
    const selectedFunctionalUnit = this.findSelectedItem(this.functionalUnitList);

    // Find selected impact category
    const selectedImpactCategory = this.findSelectedItem(this.impactCategoriesList);

    // Extract selected products (scenarios)
    const selectedScenarios = this.extractSelectedValues(this.reportScenarioList, 'idStr');

    // Find base scenario
    let baseScenario = null;
    if (this.initialReportFormData.baseItemId) {
      baseScenario = this.baseScenarioList?.find(
        (item: any) => item.idStr === String(this.initialReportFormData.baseItemId)
      );
    }
    // If no baseItemId, find the first selected product as base scenario
    if (!baseScenario) {
      baseScenario = this.findSelectedItem(this.baseScenarioList);
    }

    // Extract selected custom categories (indicators)
    const selectedIndicators = this.extractSelectedValues(this.customCategoriesList, 'id');

    // Patch form with extracted values
    this.reportForm.patchValue({
      reportTitle: reportTitle,
      reportFormate: selectedReportFormat?.id || null,
      reportScenarios: selectedScenarios.length > 0 ? selectedScenarios : null,
      functionalUnit: selectedFunctionalUnit || null,
      baseScenario: baseScenario || null,
      impactCategories: selectedImpactCategory || null,
      categoriesToInclude: selectedIndicators.length > 0 ? selectedIndicators : null,
    });

    // Handle report type if needed (from old response structure)
    if (this.initialReportFormData.newReportTypeId) {
      this.reportForm.patchValue({
        reportType: this.initialReportFormData.newReportTypeId,
      });
    }

    // Check if custom categories is selected and show the custom categories dropdown
    if (selectedImpactCategory?.name === this.CUSTOM_CATEGORIES) {
      this.isShowCustomCategories = true;
      this.validateCustomCategoriesControl();
    }

    // Check if Export Raw Data is selected
    const selectedFormatId = Number(selectedReportFormat?.id);
    if (
      selectedFormatId === this.EXPORT_RAW_DATA ||
      selectedFormatId === this.EXPORT_RAW_DATA_TYPE_4 ||
      selectedFormatId === this.EXPORT_RAW_DATA_NEW
    ) {
      this.isSelectedReportExport = true;
      const reportTypeControl = this.reportForm.get('reportType');
      reportTypeControl?.disable();
      reportTypeControl?.clearValidators();
      reportTypeControl?.updateValueAndValidity();
    }
  }

  commonInputSearchName(term: string, item: any) {
    return item.name.toLowerCase().includes(term.toLowerCase());
  }

  commonInputSearchDescription(term: string, item: any) {
    return item.description.toLowerCase().includes(term.toLowerCase());
  }

  private mapFormToPayload(formValue: any): GetProductExcelReportViewModel {
    return {
      productConfigId: +this.productConfig, // From URL parameter
      reportName: formValue.reportTitle,
      reportFormatId: formValue.reportFormate,
      reportTypeId: formValue.reportType || 0, // Set default or get from your requirements
      productIds: Array.isArray(formValue.reportScenarios)
        ? formValue.reportScenarios.join(',')
        : formValue.reportScenarios, // Handle both array and string
      funtionalUnitId:
        typeof formValue.functionalUnit === 'object'
          ? formValue.functionalUnit.id
          : formValue.functionalUnit, // Handle both object and id
      baseProductId: formValue.baseScenario?.idStr
        ? parseInt(formValue.baseScenario.idStr)
        : formValue.baseScenario?.id || 0,
      indicatorCategoryId:
        typeof formValue.impactCategories === 'object'
          ? formValue.impactCategories.id
          : formValue.impactCategories, // Handle both object and id
      indicatorIds: formValue.categoriesToInclude
        ? Array.isArray(formValue.categoriesToInclude)
          ? formValue.categoriesToInclude.join(',')
          : formValue.categoriesToInclude
        : '',
    };
  }

  async onGenerate(type?: string) {
    this.finalSubmit = true;
    if (this.reportForm.invalid) {
      this.reportForm.markAllAsTouched();
      return;
    }

    const reportFormatValue = Number(this.reportForm.get('reportFormate')?.value);
    if (
      reportFormatValue === this.EXPORT_RAW_DATA ||
      reportFormatValue === this.EXPORT_RAW_DATA_TYPE_4 ||
      reportFormatValue === this.EXPORT_RAW_DATA_NEW
    ) {
      const formValue = this.reportForm.getRawValue(); // getRawValue() includes disabled controls
      const payload = this.mapFormToPayload(formValue);

      try {
        const response =
          reportFormatValue === this.EXPORT_RAW_DATA_NEW
            ? await this._reportService.getProductExcelSummaryLinkNew(payload)
            : await this._reportService.getProductExcelSummaryLink(payload);
        if (!response.messageType) {
          const fileName = response?.data && response.data.split('/').pop();
          const fileResponse = await fetch(response.data);
          const blob = await fileResponse.blob();
          saveAs(blob, fileName);
        } else {
          this.toast.error(response.message);
        }
      } catch (error) {
        console.log('Error while generate excel', error);
        this.toast.error('Failed to generate Excel report');
      }
    } else if (this.reportForm.get('reportFormate')?.value == 1) {
      this.generateLCAReport();
    }
  }

  async generateLCAReport() {
    this._reportService.generateLCAProductReport().subscribe(
      blob => {
        const fileName = 'SlideSummaryReport.pdf';
        saveAs(blob, fileName);
        this.sharedService.show('');
      },
      error => {
        console.error('Error downloading PDF:', error);
        this.toast.error('Failed to generate PDF report');
      }
    );
  }
}
